import { Button, Fab, Menu, MenuItem, Stack, Typography } from '@mui/material'
import React, { MouseEvent, useEffect, useState } from 'react'
import { Icons } from 'utoniq-ui'

import {
  ArtistCollectionsConnectionFragment,
  Maybe,
  OrderBy,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  CollectionSelectBox,
  CollectionSelectBoxSkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  collections: Maybe<ArtistCollectionsConnectionFragment>
  sort: OrderBy
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onSort: (sort: OrderBy) => void
}

export const ConsoleCollectionsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onSort,
}) => {
  const { t } = useTranslation('consoleCollections')
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleCollections} />

      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleDashboard,
          title: t('title'),
        }}
      >
        {(state.loading || !!state.collections?.edges.length) && (
          <FilterCell onSort={onSort} sort={state.sort} />
        )}
        <Stack sx={{ px: 5, pb: 5, pt: 2 }}>
          <ScrollFetcher
            nodes={state.collections?.edges.map(({ node }) => (
              <CollectionSelectBox collection={node} key={node.id} />
            ))}
            componentProps={{ spacing: 6 }}
            loading={state.loading || state.refetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            loadingComponent={
              <Stack spacing={6}>
                {Array.from(Array(4).keys()).map(index => (
                  <CollectionSelectBoxSkeleton key={index} />
                ))}
              </Stack>
            }
            emptyMessage={t('emptyMessage')}
          />
        </Stack>
        <BaseCell />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type BaseCellProps = {}
const BaseCell: Cell<BaseCellProps> = () => {
  const { routerPages } = useRouter()
  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 100,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          href={routerPages.ConsoleCollectionCreate}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
    </Stack>
  )
}

type FilterOptions = Record<'value' | 'label', string>[]

type FilterCellProps = {
  onSort: Action['onSort']
  sort: OrderBy
}
const FilterCell: Cell<FilterCellProps> = ({ onSort, sort }) => {
  const { t } = useTranslation('consoleCollections')
  const filterOptions: FilterOptions = [
    { value: OrderBy.Desc, label: t('newestOrder') },
    { value: OrderBy.Asc, label: t('oldestOrder') },
  ]
  // sort と filterOptions の value を比較して、一致するインデックスを取得
  const initialSelectedIndex = sort
    ? filterOptions.findIndex(option => option.value === sort)
    : 0
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOption, setSelectedOption] = useState<FilterOptions[number]>(
    filterOptions[initialSelectedIndex]
  )
  const open = Boolean(anchorEl)

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = (
    _: MouseEvent<HTMLElement>,
    option: FilterOptions[number]
  ) => {
    setSelectedOption(option)
    if (option.value !== selectedOption.value) {
      onSort(option.value as OrderBy)
    }
    setAnchorEl(null)
  }
  useEffect(() => {
    const UrlParams = `?sort=${selectedOption.value}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [selectedOption])

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <CellFrame sx={{ pt: 2, px: 5 }}>
      <Stack direction="row-reverse" spacing={2}>
        <Button variant="text" sx={{ mr: -2 }} onClick={handleClickListItem}>
          <Typography variant="caption" sx={{ mr: 2 }}>
            {selectedOption.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" />
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {filterOptions.map((option, index) => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedOption.value}
            onClick={event => handleMenuItemClick(event, option)}
          >
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}
